'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocale,
  useBlockPart,
  useMarketplaceSource,
  type MarketplaceCatalogCard,
} from '@akademi/site-builder-runtime';
import { Card, CardContent, Skeleton } from '@akademi/ui';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { textTertiary } from '../_shared/styles';
import { mpSurface, formatMarketPrice } from './_shared';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { RichHtmlContent } from '../_shared/RichHtmlContent';

export interface MarketplaceCatalogHeroProps {
  /** Bound from the route's `[slug]` segment by `bindPathParams.ts` —
   *  leave empty in seed data so the real value always wins. Only set
   *  this for testing/preview on another page. */
  marketplaceItemId?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEMO_CATALOG: MarketplaceCatalogCard = {
  id: 'leadership-essentials',
  kind: 'catalog',
  title: 'Leadership Essentials',
  description: 'A 6-course bundle covering coaching, feedback and delegation for new managers.',
  courseCount: 6,
  fromPrice: { amount: '1499.00', currency: 'TRY', taxInclusive: true },
  fromListPrice: { amount: '1899.00', currency: 'TRY', taxInclusive: true },
  validity: 1,
  timeUnit: 'YEARS',
  provider: 'Akademi Learning',
  body: '<p>Six practical modules on coaching, feedback and delegation, built for first-time managers stepping into a leadership role.</p>',
  publicPath: '#',
};

const PILL_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 'var(--font-size-sm, 13px)',
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: 999,
  background: 'var(--color-surface-muted, #f4f4f5)',
  color: 'var(--color-text-secondary, #52525b)',
};

const LAYOUT_CSS = `
.mp-catalog-hero { display: grid; grid-template-columns: minmax(0, 280px) 1fr; gap: var(--space-6, 24px); align-items: start; }
@media (max-width: 640px) { .mp-catalog-hero { grid-template-columns: 1fr; } }
`;

/**
 * Marketplace catalog/bundle DETAIL hero — title/description/cover image,
 * sourced from {@link useMarketplaceSource}'s `getCatalog` (akademi-lms-v2's
 * `GET marketplace/catalog/{id}`, the catalog ENVELOPE — not the LMS's own
 * `course/details/{id}`, which a marketplace catalog/item id never resolves
 * against; confirmed live, both the entity id and the marketplaceItemId
 * 404 there). Pairs with {@link MarketplaceCoursePurchaseBox} (price/buy)
 * and {@link MarketplaceCourseGrid} (the bundle's own course list) on the
 * `marketplace-catalog-detail` reserved page — mirrors the source app,
 * which never shows richer single-course content (FAQ/curriculum/
 * comments) here regardless of whether the item is one course or a bundle.
 */
export function MarketplaceCatalogHero({
  marketplaceItemId,
  paddingOuter,
  paddingInner,
}: MarketplaceCatalogHeroProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const source = useMarketplaceSource();
  const part = useBlockPart();

  const [catalog, setCatalog] = useState<MarketplaceCatalogCard | null>(
    source && marketplaceItemId ? null : DEMO_CATALOG,
  );
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    source && marketplaceItemId ? 'loading' : 'ready',
  );

  useEffect(() => {
    if (!source || !marketplaceItemId) return;
    let cancelled = false;
    setStatus('loading');
    source
      .getCatalog(marketplaceItemId)
      .then((fetched) => {
        if (cancelled) return;
        setCatalog(fetched);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [source, marketplaceItemId]);

  const isLoading = Boolean(source && marketplaceItemId) && status === 'loading';
  const priceText = catalog?.fromPrice ? formatMarketPrice(catalog.fromPrice, locale) : undefined;
  const listPriceText = catalog?.fromListPrice
    ? formatMarketPrice(catalog.fromListPrice, locale)
    : undefined;
  const showDiscount = Boolean(listPriceText && priceText && listPriceText !== priceText);
  // Backend enum arrives as a plural noun (`"YEARS"`, `"DAYS"`, …) — strip
  // the trailing "S" to key into i18next's own singular/plural handling
  // rather than needing both forms as literal translation keys.
  const unitStem = catalog?.timeUnit?.toLowerCase().replace(/s$/, '');
  const validityText =
    catalog?.validity != null && unitStem
      ? t('marketplace.catalogHero.validity', {
          count: catalog.validity,
          unit: t(`marketplace.catalogHero.timeUnit.${unitStem}`, {
            count: catalog.validity,
            defaultValue: unitStem,
          }),
          defaultValue: '{{count}} {{unit}}',
        })
      : undefined;

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <style dangerouslySetInnerHTML={{ __html: LAYOUT_CSS }} />
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner), overflow: 'hidden' }}>
        <CardContent {...part('details')} className="p-0">
          {isLoading ? (
            <div className="mp-catalog-hero">
              <Skeleton style={{ aspectRatio: '1.4', borderRadius: 'var(--radius-md, 8px)' }} />
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)' }}
              >
                <Skeleton style={{ height: 32, width: '70%' }} />
                <Skeleton style={{ height: 16, width: '100%' }} />
                <Skeleton style={{ height: 16, width: '85%' }} />
              </div>
            </div>
          ) : status === 'error' || !catalog ? (
            <p style={{ color: 'var(--color-text-error, #b91c1c)' }}>
              {t('marketplace.catalogHero.error', {
                defaultValue: "Couldn't load this catalog. Please try again.",
              })}
            </p>
          ) : (
            <div className="mp-catalog-hero">
              <div
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 'var(--radius-md, 8px)',
                  aspectRatio: '1.4',
                }}
              >
                <CourseCoverImage image={catalog.thumbnailUrl} resizeTo="560x0" sizes="280px" />
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)' }}
              >
                <h1
                  {...part('title')}
                  style={{
                    fontSize: 'var(--font-size-2xl, 28px)',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    margin: 0,
                  }}
                >
                  {catalog.title}
                </h1>
                {catalog.description && (
                  <p
                    {...part('subtitle')}
                    style={{
                      color: 'var(--color-text-secondary, #52525b)',
                      fontSize: 'var(--font-size-md, 15px)',
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {catalog.description}
                  </p>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2, 8px)' }}>
                  {catalog.courseCount != null && catalog.courseCount > 0 && (
                    <span style={PILL_STYLE}>
                      {t('marketplace.catalogHero.courseCount', {
                        count: catalog.courseCount,
                        defaultValue: '{{count}} courses',
                      })}
                    </span>
                  )}
                  {validityText && <span style={PILL_STYLE}>{validityText}</span>}
                  {catalog.freeItem ? (
                    <span
                      style={{
                        ...PILL_STYLE,
                        background: 'var(--color-success-bg, #e4f6ed)',
                        color: 'var(--color-success-700, #1c7a4c)',
                      }}
                    >
                      {t('marketplace.cartItems.free', { defaultValue: 'Free' })}
                    </span>
                  ) : (
                    priceText && (
                      <span style={PILL_STYLE}>
                        {t('marketplace.catalogHero.fromPrice', {
                          price: priceText,
                          defaultValue: 'From {{price}}',
                        })}
                        {showDiscount && (
                          <span
                            style={{
                              ...textTertiary,
                              textDecoration: 'line-through',
                              marginLeft: 6,
                            }}
                          >
                            {listPriceText}
                          </span>
                        )}
                      </span>
                    )
                  )}
                </div>
                {catalog.provider && (
                  <p
                    style={{
                      color: 'var(--color-text-tertiary, #71717a)',
                      fontSize: 'var(--font-size-sm, 13px)',
                      margin: 0,
                    }}
                  >
                    {t('marketplace.catalogHero.provider', {
                      provider: catalog.provider,
                      defaultValue: 'By {{provider}}',
                    })}
                  </p>
                )}
              </div>
              {catalog.body && (
                <div
                  className="mp-catalog-hero__body"
                  style={{
                    gridColumn: '1 / -1',
                    paddingTop: 'var(--space-4, 16px)',
                    borderTop: '1px solid var(--color-border-default, rgba(0,0,0,0.1))',
                  }}
                >
                  <RichHtmlContent html={catalog.body} />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </SectionShell>
  );
}
